//#region src/cursor.ts
var CursorOverlay = class {
	root;
	cursor;
	agentCursor;
	waypoints = [];
	visible = false;
	last = {
		x: 0,
		y: 0
	};
	constructor(container) {
		this.root = document.createElement("div");
		this.root.setAttribute("data-backstory-cursor-layer", "");
		Object.assign(this.root.style, {
			position: "absolute",
			inset: "0",
			overflow: "hidden",
			pointerEvents: "none"
		});
		this.cursor = this.makeCursor("#111");
		this.agentCursor = this.makeCursor("#f59e0b");
		this.agentCursor.style.display = "none";
		this.root.appendChild(this.cursor);
		this.root.appendChild(this.agentCursor);
		container.appendChild(this.root);
	}
	makeCursor(color) {
		const c = document.createElement("div");
		Object.assign(c.style, {
			position: "absolute",
			left: "0",
			top: "0",
			width: "18px",
			height: "24px",
			transform: "translate(-2px,-2px)",
			transition: "none",
			willChange: "left, top",
			display: "none"
		});
		c.innerHTML = `<svg width="18" height="24" viewBox="0 0 18 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 2 L2 19 L6.5 14.5 L10 22 L13 20.5 L9.5 13 L16 13 Z" fill="${color}" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/></svg>`;
		return c;
	}
	/** Current interpolated position (last rendered). */
	get position() {
		return { ...this.last };
	}
	/** Immediately position (used on seek). Clears any pending path. */
	set(x, y, actor) {
		const el = actor ? this.agentCursor : this.cursor;
		el.style.display = "block";
		el.style.left = `${x}px`;
		el.style.top = `${y}px`;
		if (!actor) {
			this.waypoints = [{
				x,
				y,
				t: 0
			}];
			this.last = {
				x,
				y
			};
			this.visible = true;
		}
	}
	/** Schedule a move from the current position to (x,y) arriving at time `arriveAt` (session ms). */
	moveTo(x, y, now, arriveAt) {
		this.movePath([{
			x,
			y,
			t: Math.max(arriveAt, now)
		}], now);
	}
	/**
	* Append a polyline of waypoints (absolute times). Waypoints earlier than the
	* current head are dropped; the head keeps the current position at `now` so
	* motion starts from where the cursor is.
	*/
	movePath(points, now) {
		const head = {
			x: this.last.x,
			y: this.last.y,
			t: now
		};
		const future = points.filter((p) => p.t >= now).sort((a, b) => a.t - b.t);
		if (!future.length) {
			const lastPt = points[points.length - 1];
			if (lastPt) this.set(lastPt.x, lastPt.y);
			return;
		}
		const pending = this.waypoints.filter((w) => w.t > now && w.t < future[0].t);
		this.waypoints = [
			head,
			...pending,
			...future
		];
		this.visible = true;
		this.cursor.style.display = "block";
	}
	/** Waypoints still ahead of `now` (for tests and lookahead dedupe). */
	pendingWaypoints(now) {
		return this.waypoints.filter((w) => w.t > now);
	}
	/** Render interpolated position for session time `now`. */
	render(now) {
		if (!this.visible) return;
		const wps = this.waypoints;
		if (!wps.length) return;
		let x;
		let y;
		if (now <= wps[0].t) {
			x = wps[0].x;
			y = wps[0].y;
		} else if (now >= wps[wps.length - 1].t) {
			const w = wps[wps.length - 1];
			x = w.x;
			y = w.y;
			this.waypoints = [{
				x,
				y,
				t: w.t
			}];
		} else {
			let i = 1;
			while (i < wps.length && wps[i].t < now) i++;
			const a = wps[i - 1];
			const b = wps[i];
			const span = b.t - a.t;
			const p = span <= 0 ? 1 : (now - a.t) / span;
			x = a.x + (b.x - a.x) * p;
			y = a.y + (b.y - a.y) * p;
			if (i - 1 > 0) this.waypoints = wps.slice(i - 1);
		}
		this.last = {
			x,
			y
		};
		this.cursor.style.left = `${x}px`;
		this.cursor.style.top = `${y}px`;
	}
	ripple(x, y, color = "rgba(99,102,241,.55)") {
		const r = document.createElement("div");
		Object.assign(r.style, {
			position: "absolute",
			left: `${x - 12}px`,
			top: `${y - 12}px`,
			width: "24px",
			height: "24px",
			borderRadius: "50%",
			border: `3px solid ${color}`,
			opacity: "1",
			transform: "scale(.4)",
			transition: "transform .45s ease-out, opacity .45s ease-out"
		});
		r.setAttribute("data-backstory-ripple", "");
		this.root.appendChild(r);
		(typeof requestAnimationFrame === "function" ? requestAnimationFrame : (fn) => setTimeout(fn, 0))(() => {
			r.style.transform = "scale(1.6)";
			r.style.opacity = "0";
		});
		setTimeout(() => r.remove(), 500);
	}
	hide() {
		this.visible = false;
		this.cursor.style.display = "none";
		this.agentCursor.style.display = "none";
	}
	clearRipples() {
		for (const c of Array.from(this.root.children)) if (c !== this.cursor && c !== this.agentCursor) c.remove();
	}
	destroy() {
		this.root.remove();
	}
};
//#endregion
//#region src/selection.ts
/** Default half-width when a selection is created around the playhead. */
var DEFAULT_HALF_MS = 15e3;
/** Step used by keyboard extension (shift + arrow). */
var KEY_STEP_MS = 1e3;
var SelectionModel = class {
	range = null;
	anchor = null;
	dragStart = null;
	listeners = /* @__PURE__ */ new Set();
	/** Upper bound (session duration); updated by the Player when it loads. */
	durationMs = 0;
	get() {
		return this.range;
	}
	/** Whether a selection exists and spans at least 1 ms. */
	get active() {
		return !!this.range && this.range.toMs > this.range.fromMs;
	}
	onChange(fn) {
		this.listeners.add(fn);
		return () => this.listeners.delete(fn);
	}
	/** Set an explicit range (normalized and clamped) or clear with null. */
	set(range) {
		const next = range ? this.normalize(range) : null;
		if (sameRange(next, this.range)) return;
		this.range = next;
		for (const l of this.listeners) l(next);
	}
	clear() {
		this.anchor = null;
		this.dragStart = null;
		this.set(null);
	}
	/** ±half around a center point, clamped to [0, duration]. */
	setAround(centerMs, halfMs = DEFAULT_HALF_MS) {
		this.anchor = null;
		this.set({
			fromMs: centerMs - halfMs,
			toMs: centerMs + halfMs
		});
		return this.range;
	}
	/** Move only the start edge to t (keeps the end, swapping if needed). */
	setStart(t) {
		const end = this.range?.toMs ?? t;
		this.set({
			fromMs: t,
			toMs: Math.max(end, t)
		});
	}
	/** Move only the end edge to t (keeps the start, swapping if needed). */
	setEnd(t) {
		const start = this.range?.fromMs ?? t;
		this.set({
			fromMs: Math.min(start, t),
			toMs: t
		});
	}
	/**
	* Shift-click semantics: the first click sets an anchor (a zero-length
	* range), the second completes the range between anchor and click.
	*/
	shiftClick(t) {
		if (this.anchor === null) {
			this.anchor = t;
			this.set({
				fromMs: t,
				toMs: t
			});
			return;
		}
		const a = this.anchor;
		this.anchor = null;
		this.set({
			fromMs: Math.min(a, t),
			toMs: Math.max(a, t)
		});
	}
	/** Drag lifecycle from a pointer on the scrubber. */
	beginDrag(t) {
		this.dragStart = t;
		this.anchor = null;
		this.set({
			fromMs: t,
			toMs: t
		});
	}
	drag(t) {
		if (this.dragStart === null) return;
		this.set({
			fromMs: Math.min(this.dragStart, t),
			toMs: Math.max(this.dragStart, t)
		});
	}
	endDrag() {
		this.dragStart = null;
		if (this.range && this.range.toMs === this.range.fromMs) this.set(null);
	}
	/**
	* Keyboard extension: grow or shrink an edge by `deltaMs` (negative shrinks).
	* With no selection, starts one at `playheadMs`.
	*/
	extend(edge, deltaMs, playheadMs) {
		const r = this.range ?? {
			fromMs: playheadMs,
			toMs: playheadMs
		};
		if (edge === "end") this.set({
			fromMs: r.fromMs,
			toMs: Math.max(r.fromMs, r.toMs + deltaMs)
		});
		else this.set({
			fromMs: Math.min(r.toMs, r.fromMs + deltaMs),
			toMs: r.toMs
		});
	}
	normalize(r) {
		const max = this.durationMs > 0 ? this.durationMs : Number.POSITIVE_INFINITY;
		const clamp = (v) => Math.max(0, Math.min(max, Math.round(v)));
		const a = clamp(r.fromMs);
		const b = clamp(r.toMs);
		return {
			fromMs: Math.min(a, b),
			toMs: Math.max(a, b)
		};
	}
};
function sameRange(a, b) {
	if (a === b) return true;
	if (!a || !b) return false;
	return a.fromMs === b.fromMs && a.toMs === b.toMs;
}
/** Human label like "1:02 – 1:32 (30 s)". */
function formatRange(r, fmt) {
	const span = Math.round((r.toMs - r.fromMs) / 1e3);
	return `${fmt(r.fromMs)} – ${fmt(r.toMs)} (${span} s)`;
}
//#endregion
//#region src/canvas/commands.ts
var MARK = {
	state: "__state",
	frame: "__frame",
	source: "__src",
	path: "__path"
};
var defaultImageDecoder = {
	async decode(data) {
		try {
			const bin = atob(data);
			const bytes = new Uint8Array(bin.length);
			for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
			const blob = new Blob([bytes], { type: "image/webp" });
			if (typeof createImageBitmap === "function") return await createImageBitmap(blob);
			const url = URL.createObjectURL(blob);
			try {
				return await loadImage(url);
			} finally {
				URL.revokeObjectURL(url);
			}
		} catch {
			return null;
		}
	},
	load(url) {
		return loadImage(url);
	}
};
function loadImage(url) {
	return new Promise((resolve) => {
		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => resolve(img);
		img.onerror = () => resolve(null);
		img.src = url;
	});
}
var CommandReplayer = class CommandReplayer {
	ctx;
	decoder;
	onDraw;
	/** Matches the recorder's source LRU: old sources are re-announced if reused. */
	static MAX_IMAGES = 64;
	grads = /* @__PURE__ */ new Map();
	paths = /* @__PURE__ */ new Map();
	images = /* @__PURE__ */ new Map();
	pendingImages = /* @__PURE__ */ new Map();
	/** Commands waiting for an image still decoding are replayed once it arrives. */
	deferred = [];
	destroyed = false;
	frames = 0;
	constructor(ctx, decoder = defaultImageDecoder, onDraw) {
		this.ctx = ctx;
		this.decoder = decoder;
		this.onDraw = onDraw;
	}
	/** Apply one batch (one recorded animation frame). */
	apply(cmds) {
		for (const c of cmds) this.applyOne(c);
		this.onDraw?.();
	}
	reset() {
		this.grads.clear();
		this.paths.clear();
		this.deferred = [];
		try {
			this.ctx.reset?.();
			this.ctx.setTransform?.(1, 0, 0, 1, 0, 0);
			this.ctx.clearRect?.(0, 0, this.ctx.canvas?.width ?? 1e5, this.ctx.canvas?.height ?? 1e5);
		} catch {}
	}
	/** Release decoded GPU resources when a replay track disappears. */
	destroy() {
		this.destroyed = true;
		for (const image of this.images.values()) closeImage(image);
		this.images.clear();
		this.pendingImages.clear();
		this.deferred = [];
		this.grads.clear();
		this.paths.clear();
	}
	applyOne(c) {
		switch (c.f) {
			case MARK.frame:
				this.frames++;
				return;
			case MARK.state: return;
			case MARK.source:
				this.registerSource(c.p);
				return;
			case MARK.path:
				this.registerPath(c.p);
				return;
			case "set": {
				const [prop, value] = c.a ?? [];
				if (typeof prop === "string") try {
					this.ctx[prop] = this.resolve(value);
				} catch {}
				return;
			}
			case "g.addColorStop": {
				const [g, off, color] = c.a ?? [];
				this.resolve(g)?.addColorStop?.(off, color);
				return;
			}
		}
		const fn = this.ctx[c.f];
		if (typeof fn !== "function") return;
		const args = (c.a ?? []).map((a) => this.resolve(a));
		const pendingRef = (c.a ?? []).find((a) => isImgRef(a) && this.pendingImages.has(a.$img));
		if (pendingRef) {
			this.deferred.push(c);
			this.pendingImages.get(pendingRef.$img).then(() => this.replayDeferred());
			return;
		}
		if (args.some((a, i) => isImgRef((c.a ?? [])[i]) && (a == null || a.masked))) {
			if (c.f === "drawImage") this.maskedBox(args);
			return;
		}
		try {
			const result = fn.apply(this.ctx, args);
			const ref = c.p?.ref;
			if (ref && result && typeof result === "object") this.grads.set(ref, result);
		} catch {}
	}
	replayDeferred() {
		const d = this.deferred;
		this.deferred = [];
		for (const c of d) this.applyOne(c);
		this.onDraw?.();
	}
	maskedBox(args) {
		const nums = args.filter((a) => typeof a === "number");
		let dx = 0, dy = 0, dw = 0, dh = 0;
		if (nums.length >= 8) [dx, dy, dw, dh] = [
			nums[4],
			nums[5],
			nums[6],
			nums[7]
		];
		else if (nums.length >= 4) [dx, dy, dw, dh] = [
			nums[0],
			nums[1],
			nums[2],
			nums[3]
		];
		else if (nums.length >= 2) {
			const meta = this.images.get(args[0]?.$img ?? "");
			[dx, dy, dw, dh] = [
				nums[0],
				nums[1],
				meta?.w ?? 32,
				meta?.h ?? 32
			];
		}
		try {
			this.ctx.save?.();
			this.ctx.fillStyle = "#9ca3af";
			this.ctx.fillRect?.(dx, dy, dw, dh);
			this.ctx.restore?.();
		} catch {}
	}
	resolve(a) {
		if (a && typeof a === "object") {
			const o = a;
			if (o.$g) return this.grads.get(o.$g);
			if (o.$p) return this.paths.get(o.$p);
			if (o.$img !== void 0) return this.images.get(o.$img) ?? null;
			if (Array.isArray(a) && a.length === 6 && a.every((n) => typeof n === "number") && typeof DOMMatrix === "function") return new DOMMatrix(a);
		}
		return a;
	}
	registerSource(p) {
		if (this.destroyed || !p || this.images.has(p.ref) || this.pendingImages.has(p.ref)) return;
		if (p.masked) {
			this.rememberImage(p.ref, {
				masked: true,
				w: p.w,
				h: p.h
			});
			return;
		}
		const job = (p.data ? this.decoder.decode(p.data, p.w, p.h) : p.url ? this.decoder.load(p.url) : Promise.resolve(null)).then((img) => {
			if (this.destroyed) {
				closeImage(img);
				return;
			}
			this.rememberImage(p.ref, img);
			this.pendingImages.delete(p.ref);
		});
		this.pendingImages.set(p.ref, job);
	}
	rememberImage(ref, image) {
		const previous = this.images.get(ref);
		if (previous && previous !== image) closeImage(previous);
		this.images.delete(ref);
		this.images.set(ref, image);
		while (this.images.size > CommandReplayer.MAX_IMAGES) {
			const oldest = this.images.keys().next().value;
			if (oldest === void 0) break;
			const evicted = this.images.get(oldest);
			this.images.delete(oldest);
			closeImage(evicted);
		}
	}
	registerPath(p) {
		if (!p || typeof Path2D !== "function") return;
		try {
			const path = p.svg ? new Path2D(p.svg) : new Path2D();
			for (const op of p.ops ?? []) {
				const fn = path[op.f];
				if (typeof fn !== "function") continue;
				const args = (op.a ?? []).map((a) => {
					const o = a;
					if (o && typeof o === "object" && (o.$ops || o.$svg)) {
						const sub = o.$svg ? new Path2D(o.$svg) : new Path2D();
						for (const so of o.$ops ?? []) sub[so.f]?.(...so.a ?? []);
						return sub;
					}
					return a;
				});
				fn.apply(path, args);
			}
			this.paths.set(p.ref, path);
		} catch {}
	}
};
function isImgRef(a) {
	return !!a && typeof a === "object" && typeof a.$img === "string";
}
function closeImage(image) {
	try {
		if (image && typeof image.close === "function") image.close();
	} catch {}
}
//#endregion
//#region src/canvas/video.ts
function b64bytes(data) {
	const bin = atob(data);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}
var CODEC_STRINGS = {
	av1: "av01.0.04M.08",
	vp9: "vp09.00.10.08",
	h264: "avc1.42001f"
};
/** Still-image codecs. iOS screenshots are JPEG; Android screenshots are WebP. */
var IMAGE_TYPES = {
	webp: "image/webp",
	jpeg: "image/jpeg"
};
/** Default decoder: WebCodecs VideoDecoder for av1/vp9/h264, createImageBitmap for webp and jpeg. */
var WebCodecsFrameDecoder = class {
	decoder = null;
	codecKey = "";
	last = null;
	async decodeSequence(frames) {
		if (!frames.length) return null;
		const lastEv = frames[frames.length - 1];
		const imageType = IMAGE_TYPES[lastEv.codec];
		if (imageType) try {
			const blob = new Blob([b64bytes(lastEv.data)], { type: imageType });
			return typeof createImageBitmap === "function" ? await createImageBitmap(blob) : null;
		} catch {
			return null;
		}
		const VD = globalThis.VideoDecoder;
		const EVC = globalThis.EncodedVideoChunk;
		if (!VD || !EVC) return null;
		const first = frames[0];
		const codec = CODEC_STRINGS[first.codec] ?? first.codec;
		const key = `${codec}:${first.w}x${first.h}:${first.desc ?? ""}`;
		if (!this.decoder || this.codecKey !== key || this.decoder.state === "closed") {
			this.reset();
			const decoder = new VD({
				output: (f) => {
					this.last?.close();
					this.last = f;
				},
				error: () => void 0
			});
			const cfg = {
				codec,
				codedWidth: first.w,
				codedHeight: first.h
			};
			if (first.desc) cfg.description = b64bytes(first.desc);
			try {
				decoder.configure(cfg);
			} catch {
				return null;
			}
			this.decoder = decoder;
			this.codecKey = key;
		}
		try {
			for (const f of frames) this.decoder.decode(new EVC({
				type: f.keyframe ? "key" : "delta",
				timestamp: Math.round((f.ts ?? f.t) * 1e3),
				data: b64bytes(f.data)
			}));
			await this.decoder.flush();
		} catch {
			return null;
		}
		return this.last;
	}
	reset() {
		try {
			this.decoder?.close();
		} catch {}
		this.decoder = null;
		this.codecKey = "";
		this.last?.close();
		this.last = null;
	}
};
/**
* Video track: keeps the frames seen for one canvas, and on flush decodes from
* the nearest keyframe at or before the target time to the last frame ≤ target.
*/
var VideoTrack = class {
	ctx;
	decoder;
	onDraw;
	frames = [];
	lastDrawnKey = "";
	busy = null;
	constructor(ctx, decoder, onDraw) {
		this.ctx = ctx;
		this.decoder = decoder;
		this.onDraw = onDraw;
	}
	/** Record a frame; during live playback (not seeking) draw immediately. */
	push(f, absT, seeking) {
		this.frames.push({
			...f,
			t: absT
		});
		if (!seeking) this.flush(absT);
	}
	reset() {
		this.frames = [];
		this.lastDrawnKey = "";
		this.decoder.reset();
	}
	/** The surface was resized and its bitmap cleared, so the next flush redraws. */
	invalidate() {
		this.lastDrawnKey = "";
	}
	/** Bring the canvas to the frame at `absT` (nearest keyframe ≤ target, rolled forward). */
	flush(absT) {
		const run = async () => {
			const idx = lastIndexAtOrBefore(this.frames, absT);
			if (idx < 0) return;
			let k = idx;
			while (k > 0 && !this.frames[k].keyframe) k--;
			const seq = this.frames.slice(k, idx + 1);
			const key = `${k}:${idx}`;
			if (key === this.lastDrawnKey) return;
			const img = await this.decoder.decodeSequence(seq);
			this.lastDrawnKey = key;
			if (!img) return;
			const last = seq[seq.length - 1];
			try {
				const cw = this.ctx.canvas?.width ?? last.w;
				const ch = this.ctx.canvas?.height ?? last.h;
				this.ctx.clearRect?.(0, 0, cw, ch);
				this.ctx.drawImage?.(img, 0, 0, cw, ch);
				this.onDraw?.();
			} catch {} finally {
				try {
					img.close?.();
				} catch {}
			}
		};
		const p = (this.busy ?? Promise.resolve()).then(run, run);
		this.busy = p.finally(() => {
			if (this.busy === p) this.busy = null;
		});
		return p;
	}
	count() {
		return this.frames.length;
	}
};
function lastIndexAtOrBefore(frames, t) {
	let lo = 0;
	let hi = frames.length - 1;
	let ans = -1;
	while (lo <= hi) {
		const mid = lo + hi >> 1;
		if (frames[mid].t <= t) {
			ans = mid;
			lo = mid + 1;
		} else hi = mid - 1;
	}
	return ans;
}
//#endregion
//#region src/canvas/tracks.ts
var OVERLAY_ATTR = "data-backstory-canvas-gap";
/**
* The mirrored node for a canvas id, only when it really is an element. A
* replayed id can land on a text or comment node — a mutation replaced the
* canvas, or a snapshot is mid-rebuild — and element APIs would throw on it.
*/
function elementFor(mirror, id) {
	const node = mirror.getNode(id);
	return node && node.nodeType === 1 ? node : null;
}
/**
* Canvas playback (PLAN §6.6): one track per canvas node. Command tracks
* re-execute recorded calls; video tracks decode frames. Seeking snaps to the
* nearest keyframe and rolls forward on flush. Gaps show a hatched overlay with
* the reason (bandwidth, hidden, not allowlisted, ...).
*/
var CanvasTracks = class {
	mirror;
	opts;
	tracks = /* @__PURE__ */ new Map();
	hidePhi;
	constructor(mirror, opts = {}) {
		this.mirror = mirror;
		this.opts = opts;
		this.hidePhi = !!opts.hidePhi;
	}
	setHidePhi(v) {
		this.hidePhi = v;
		for (const t of this.tracks.values()) this.paint(t);
	}
	/** Snapshot rebuilt: forget everything (placeholders are new nodes). */
	reset() {
		for (const t of this.tracks.values()) {
			t.video?.reset();
			t.cmds?.destroy();
			if (t.url) revoke(t.url);
		}
		this.tracks.clear();
	}
	apply(e, absT, seeking) {
		const t = this.track(e.id);
		if (e.phi === true) t.phi = true;
		if (e.type === "canvas_cmds") {
			const first = e.cmds[0];
			if (first && first.f === MARK.state) {
				const p = first.p;
				t.recording = !!p.recording;
				t.reason = p.reason ?? "";
				if (p.w && p.h) {
					t.w = p.w;
					t.h = p.h;
				}
				this.ensureCanvas(t);
				this.paint(t);
				if (e.cmds.length === 1) return;
			}
			if (this.hidePhi && t.phi) return;
			if (seeking) t.seekQueue.push(e.cmds);
			else this.replayCmds(t, e.cmds);
		} else {
			if (this.hidePhi && t.phi) return;
			if (e.w && e.h) {
				t.w = e.w;
				t.h = e.h;
			}
			t.recording = true;
			this.ensureCanvas(t);
			if (elementFor(this.mirror, e.id)?.getAttribute("data-backstory-placeholder") !== "video") this.coverVisit(t);
			if (!t.video && t.ctx) t.video = new VideoTrack(t.ctx, (this.opts.frameDecoder ?? (() => new WebCodecsFrameDecoder()))(), () => this.present(t));
			t.video?.push(e, absT, seeking);
		}
	}
	/** After a seek: replay queued command batches and bring video tracks to the target time. */
	flush(absT) {
		for (const t of this.tracks.values()) {
			if (t.seekQueue.length) {
				const queue = t.seekQueue;
				t.seekQueue = [];
				this.ensureCanvas(t);
				if (t.cmds) {
					t.cmds.reset();
					for (const cmds of queue) t.cmds.apply(cmds);
				}
			}
			t.video?.flush(absT);
		}
	}
	/** Test/inspection hook. */
	info(id) {
		const t = this.tracks.get(id);
		if (!t) return null;
		return {
			recording: t.recording,
			reason: t.reason,
			frames: t.video?.count() ?? 0,
			commandFrames: t.cmds?.frames ?? 0,
			phi: t.phi
		};
	}
	track(id) {
		let t = this.tracks.get(id);
		if (!t) {
			t = {
				id,
				canvas: null,
				img: null,
				ctx: null,
				cmds: null,
				video: null,
				recording: false,
				reason: "",
				phi: false,
				w: 0,
				h: 0,
				presenting: false,
				dirty: false,
				url: null,
				seekQueue: []
			};
			this.tracks.set(id, t);
		}
		return t;
	}
	replayCmds(t, cmds) {
		this.ensureCanvas(t);
		t.cmds?.apply(cmds);
	}
	/**
	* Prepare a track for drawing: an `<img>` inside the mirrored placeholder,
	* and a drawing surface for it.
	*
	* The surface deliberately lives in the player's own document. The replay
	* frame is sandboxed without `allow-scripts`, and a `<canvas>` in a document
	* with scripting disabled renders its fallback content instead of its bitmap
	* (HTML spec, "the canvas element"), so anything drawn there stays invisible.
	* Each finished frame is therefore re-encoded and handed to the `<img>`,
	* which the frame's CSP allows (`img-src * data: blob:`).
	*/
	ensureCanvas(t) {
		const host = elementFor(this.mirror, t.id);
		if (!host || !host.ownerDocument) return;
		const doc = host.ownerDocument;
		let img = host.querySelector(":scope > img[data-backstory-track]");
		if (!img) {
			img = doc.createElement("img");
			img.setAttribute("data-backstory-track", "");
			img.setAttribute("alt", "");
			Object.assign(img.style, {
				display: "block",
				width: "100%",
				height: "100%",
				objectFit: "fill"
			});
			host.textContent = "";
			host.style.position = host.style.position || "relative";
			host.style.lineHeight = "normal";
			host.appendChild(img);
		}
		t.img = img;
		const w = t.w || Number(host.getAttribute("width")) || host.clientWidth || 300;
		const h = t.h || Number(host.getAttribute("height")) || host.clientHeight || 150;
		const canvas = t.canvas ?? this.ownerDoc().createElement("canvas");
		if (canvas.width !== w || canvas.height !== h) {
			canvas.width = w;
			canvas.height = h;
			t.video?.invalidate();
		}
		if (t.canvas === canvas && t.ctx) return;
		t.canvas = canvas;
		const ctx = this.opts.getContext ? this.opts.getContext(canvas) : canvas.getContext("2d");
		t.ctx = ctx;
		if (ctx && !t.cmds) t.cmds = new CommandReplayer(ctx, this.opts.imageDecoder ?? defaultImageDecoder, () => this.present(t));
		if (t.video && ctx) t.video.ctx = ctx;
	}
	/**
	* A full-visit screenshot is attached to the root view, which fills the
	* page. Stretch that node to the frame so the picture is the visit, at the
	* size it was taken, rather than a thumbnail inside one control.
	*/
	coverVisit(t) {
		const host = elementFor(this.mirror, t.id);
		if (!host || !t.w || !t.h) return;
		const doc = host.ownerDocument;
		const root = doc.documentElement;
		const body = doc.body;
		if (host !== root && host !== body && host.parentElement !== body && host.parentElement !== root) return;
		host.style.position = "absolute";
		host.style.left = "0";
		host.style.top = "0";
		host.style.width = `${t.w}px`;
		host.style.height = `${t.h}px`;
		host.style.overflow = "hidden";
	}
	/** The player's document, where drawing surfaces are created. */
	ownerDoc() {
		return this.mirror.iframe.ownerDocument ?? document;
	}
	/**
	* Push the latest drawing into the mirrored `<img>`. Encodes are coalesced:
	* frames that land while one is in flight collapse into a single redraw.
	*/
	present(t) {
		this.paint(t);
		const { img, canvas } = t;
		if (!img || !canvas) return;
		if (t.presenting) {
			t.dirty = true;
			return;
		}
		t.presenting = true;
		const show = (src) => {
			const previous = t.url;
			t.url = src.startsWith("blob:") ? src : null;
			const settle = () => {
				if (previous) revoke(previous);
				t.presenting = false;
				if (t.dirty) {
					t.dirty = false;
					this.present(t);
				}
			};
			img.onload = settle;
			img.onerror = settle;
			img.src = src;
		};
		try {
			if (typeof canvas.toBlob === "function") canvas.toBlob((blob) => blob ? show(URL.createObjectURL(blob)) : void (t.presenting = false), "image/webp", .92);
			else show(canvas.toDataURL());
		} catch {
			t.presenting = false;
		}
	}
	/** Hatched overlay + tooltip while the canvas was not recorded, or PHI is hidden. */
	paint(t) {
		const host = elementFor(this.mirror, t.id);
		if (!host) return;
		const hiddenPhi = this.hidePhi && t.phi;
		if (t.recording && !hiddenPhi) {
			host.removeAttribute(OVERLAY_ATTR);
			host.removeAttribute("title");
			host.style.background = "none";
			if (t.img) t.img.style.opacity = "1";
			return;
		}
		const reason = hiddenPhi ? "PHI canvas hidden" : `canvas not recorded: ${reasonLabel(t.reason)}`;
		host.setAttribute(OVERLAY_ATTR, hiddenPhi ? "phi" : t.reason || "unknown");
		host.setAttribute("title", reason);
		host.style.background = "repeating-linear-gradient(45deg, rgba(148,163,184,.35) 0 8px, rgba(148,163,184,.15) 8px 16px)";
		if (t.img) t.img.style.opacity = hiddenPhi ? "0" : "0.6";
	}
};
function revoke(url) {
	try {
		URL.revokeObjectURL(url);
	} catch {}
}
function reasonLabel(reason) {
	switch (reason) {
		case "offscreen": return "not on screen";
		case "hidden": return "hidden";
		case "page-hidden": return "tab in background";
		case "not-allowlisted":
		case "strict-mode": return "not allowlisted";
		case "level-off":
		case "too-large": return "bandwidth";
		case "detached": return "removed from page";
		case "zero-size": return "zero size";
		default: return reason || "unknown";
	}
}
//#endregion
//#region src/highlight.ts
var HighlightOverlay = class {
	stage;
	root;
	boxes = [];
	timers = /* @__PURE__ */ new Set();
	inspectLayer = null;
	constructor(stage) {
		this.stage = stage;
		this.root = document.createElement("div");
		this.root.setAttribute("data-backstory-highlight", "");
		Object.assign(this.root.style, {
			position: "absolute",
			inset: "0",
			pointerEvents: "none",
			zIndex: "5"
		});
		stage.appendChild(this.root);
	}
	/** Draw boxes for `ms` (0 = until cleared). */
	show(boxes, opts = {}) {
		this.clear();
		const color = opts.color ?? "rgba(99,102,241,.9)";
		boxes.forEach((b, i) => {
			const d = document.createElement("div");
			d.setAttribute("data-backstory-highlight-box", "");
			Object.assign(d.style, {
				position: "absolute",
				left: `${b.x}px`,
				top: `${b.y}px`,
				width: `${Math.max(2, b.w)}px`,
				height: `${Math.max(2, b.h)}px`,
				outline: `2px solid ${color}`,
				background: color.replace(/[\d.]+\)$/, ".12)"),
				boxSizing: "border-box",
				borderRadius: "2px"
			});
			if (opts.label && i === 0) {
				const tag = document.createElement("span");
				tag.textContent = opts.label;
				Object.assign(tag.style, {
					position: "absolute",
					left: "0",
					top: "-18px",
					background: color,
					color: "#fff",
					font: "10px/16px system-ui, sans-serif",
					padding: "0 4px",
					borderRadius: "3px",
					whiteSpace: "nowrap"
				});
				d.appendChild(tag);
			}
			this.root.appendChild(d);
			this.boxes.push(d);
		});
		if (opts.ms && opts.ms > 0) {
			const t = setTimeout(() => {
				this.timers.delete(t);
				this.clear();
			}, opts.ms);
			this.timers.add(t);
		}
	}
	clear() {
		for (const b of this.boxes) b.remove();
		this.boxes = [];
	}
	/** Inspect mode: a transparent layer that reports pointer position over the stage. */
	setInspect(enabled, onMove, onPick) {
		if (!enabled) {
			this.inspectLayer?.remove();
			this.inspectLayer = null;
			this.clear();
			return;
		}
		if (this.inspectLayer) return;
		const layer = document.createElement("div");
		layer.setAttribute("data-backstory-inspect", "");
		Object.assign(layer.style, {
			position: "absolute",
			inset: "0",
			cursor: "crosshair",
			zIndex: "6"
		});
		const pos = (e) => {
			const r = this.stage.getBoundingClientRect();
			const sx = r.width ? this.stage.offsetWidth / r.width : 1;
			const sy = r.height ? this.stage.offsetHeight / r.height : 1;
			return [(e.clientX - r.left) * sx, (e.clientY - r.top) * sy];
		};
		layer.addEventListener("mousemove", (e) => {
			const [x, y] = pos(e);
			onMove(x, y);
		});
		layer.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();
			const [x, y] = pos(e);
			onPick(x, y);
		});
		this.stage.appendChild(layer);
		this.inspectLayer = layer;
	}
	destroy() {
		for (const t of this.timers) clearTimeout(t);
		this.timers.clear();
		this.inspectLayer?.remove();
		this.root.remove();
	}
};
//#endregion
//#region src/mirror/sanitize.ts
/**
* Sanitization rules for rebuilding recorded DOM inside the sandboxed iframe.
* The iframe already has `sandbox="allow-same-origin"` (no scripts) and a CSP
* meta tag, but we strip anything executable anyway: defense in depth.
*/
var BLOCKED_TAGS = /* @__PURE__ */ new Set([
	"script",
	"noscript",
	"object",
	"embed",
	"applet",
	"meta"
]);
/** Tags whose recorded box is rendered as a placeholder. */
var PLACEHOLDER_TAGS = /* @__PURE__ */ new Set([
	"canvas",
	"video",
	"audio",
	"iframe",
	"frame",
	"portal"
]);
var URL_ATTRS = /* @__PURE__ */ new Set([
	"href",
	"src",
	"xlink:href",
	"action",
	"formaction",
	"poster",
	"data",
	"srcset"
]);
var CSP_META = "default-src 'none'; img-src * data: blob:; style-src 'unsafe-inline' *; font-src * data:; media-src *";
function isBlockedTag(tag) {
	return BLOCKED_TAGS.has(tag.toLowerCase());
}
/** Returns null when the attribute must be dropped, otherwise the safe value. */
function sanitizeAttr(tag, name, value) {
	const n = name.toLowerCase();
	if (n.startsWith("on")) return null;
	if (n === "srcdoc") return null;
	if (URL_ATTRS.has(n)) {
		const v = value.trim().toLowerCase().replace(/[\u0000-\u0020]/g, "");
		if (v.startsWith("javascript:") || v.startsWith("vbscript:")) return null;
		if (v.startsWith("data:text/html")) return null;
	}
	if (tag === "a" && n === "target") return "_blank";
	if (tag === "form" && n === "action") return null;
	if (tag === "link" && n === "rel" && value.toLowerCase().includes("import")) return null;
	return value;
}
/** Style text is safe under CSP but we strip behaviors/expressions/imports for old engines. */
function sanitizeCss(css) {
	return css.replace(/expression\s*\(/gi, "expression-blocked(").replace(/behavior\s*:/gi, "behavior-blocked:");
}
//#endregion
//#region src/mirror/mirror.ts
var SVG_NS = "http://www.w3.org/2000/svg";
var REWRITE_ATTRS = /* @__PURE__ */ new Set([
	"src",
	"href",
	"poster",
	"srcset",
	"xlink:href"
]);
/** Apply an AssetRewriter to a `srcset` attribute value. */
function rewriteSrcset(value, rewrite) {
	return value.split(",").map((part) => {
		const trimmed = part.trim();
		if (!trimmed) return trimmed;
		const sp = trimmed.search(/\s/);
		const url = sp === -1 ? trimmed : trimmed.slice(0, sp);
		const rest = sp === -1 ? "" : trimmed.slice(sp);
		return rewrite(url) + rest;
	}).join(", ");
}
/** Apply an AssetRewriter to every url(...) inside CSS text. */
function rewriteCssUrls(css, rewrite) {
	return css.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/g, (_m, q, url) => {
		if (/^(data:|blob:|#)/i.test(url)) return `url(${q}${url}${q})`;
		return `url(${q}${rewrite(url)}${q})`;
	});
}
/**
* Mirror owns the sandboxed iframe and the id→Node map. It knows nothing about
* time; the Player drives it.
*/
/** A blank HTML document in an iframe, including the doctype the snapshot registers. */
function installHtmlShell(doc) {
	doc.open();
	doc.appendChild(doc.implementation.createDocumentType("html", "", ""));
	const html = doc.createElement("html");
	html.append(doc.createElement("head"), doc.createElement("body"));
	doc.appendChild(html);
	doc.close();
}
var Mirror = class {
	iframe;
	map = /* @__PURE__ */ new Map();
	reverse = /* @__PURE__ */ new WeakMap();
	baseUrl;
	ready = false;
	assetRewriter;
	constructor(container, opts = {}) {
		this.baseUrl = opts.baseUrl;
		this.assetRewriter = opts.assetRewriter;
		this.iframe = document.createElement("iframe");
		this.iframe.setAttribute("sandbox", "allow-same-origin");
		this.iframe.setAttribute("title", "Backstory replay");
		this.iframe.setAttribute("aria-hidden", "true");
		this.iframe.style.border = "0";
		this.iframe.style.background = "transparent";
		this.iframe.style.pointerEvents = "none";
		this.iframe.style.display = "block";
		this.iframe.tabIndex = -1;
		container.appendChild(this.iframe);
	}
	get doc() {
		const d = this.iframe.contentDocument;
		if (!d) throw new Error("mirror iframe has no document");
		return d;
	}
	getNode(id) {
		return this.map.get(id);
	}
	getId(node) {
		return this.reverse.get(node);
	}
	get size() {
		return this.map.size;
	}
	setViewport(w, h) {
		this.iframe.style.width = `${w}px`;
		this.iframe.style.height = `${h}px`;
	}
	/** Full rebuild from a snapshot. Clears the map. */
	applySnapshot(snap) {
		this.baseUrl = snap.url || this.baseUrl;
		this.iframe.style.colorScheme = "";
		this.iframe.style.background = "transparent";
		this.map.clear();
		this.reverse = /* @__PURE__ */ new WeakMap();
		const doc = this.doc;
		installHtmlShell(doc);
		this.ready = true;
		const root = snap.node;
		this.register(root.id, doc);
		for (const child of root.childNodes) {
			if (child.type === "doctype") {
				if (doc.doctype) this.register(child.id, doc.doctype);
				continue;
			}
			if (child.type === "element" && child.tag.toLowerCase() === "html") {
				const html = doc.documentElement;
				this.register(child.id, html);
				this.applyAttrs(html, child, "html");
				this.syncRecordedColorScheme(html, child.attrs["data-backstory-replay-color-scheme"] ?? null);
				for (const c of child.childNodes) if (c.type === "element" && (c.tag.toLowerCase() === "head" || c.tag.toLowerCase() === "body")) {
					const target = c.tag.toLowerCase() === "head" ? doc.head : doc.body;
					this.register(c.id, target);
					this.applyAttrs(target, c, c.tag.toLowerCase());
					for (const gc of c.childNodes) {
						const built = this.build(gc, false);
						if (built) target.appendChild(built);
					}
				} else {
					const built = this.build(c, false);
					if (built) html.appendChild(built);
				}
				continue;
			}
			const built = this.build(child, false);
			if (built) doc.appendChild(built);
		}
		this.injectHead(doc, root);
		doc.documentElement.scrollLeft = snap.scroll.x;
		doc.documentElement.scrollTop = snap.scroll.y;
	}
	injectHead(doc, root) {
		const head = doc.head;
		const csp = doc.createElement("meta");
		csp.setAttribute("http-equiv", "Content-Security-Policy");
		csp.setAttribute("content", CSP_META);
		head.insertBefore(csp, head.firstChild);
		if (this.baseUrl) {
			const base = doc.createElement("base");
			base.setAttribute("href", this.baseUrl);
			head.insertBefore(base, csp.nextSibling);
		}
		if (root.adoptedStyles?.length) {
			const style = doc.createElement("style");
			style.setAttribute("data-backstory-adopted", "");
			style.textContent = root.adoptedStyles.map((c) => this.css(sanitizeCss(c))).join("\n");
			head.appendChild(style);
		}
		const rw = doc.createElement("style");
		rw.setAttribute("data-backstory-player", "");
		rw.textContent = `
      [data-backstory-placeholder]{display:inline-block;background:repeating-linear-gradient(45deg,#e5e7eb 0 8px,#f3f4f6 8px 16px);color:#6b7280;font:12px system-ui;text-align:center;overflow:hidden;box-sizing:border-box;border:1px dashed #9ca3af}
      [data-backstory-blocked]{display:inline-block;background:#e5e7eb;box-sizing:border-box}
      html{scroll-behavior:auto!important}
      *{animation-play-state:paused!important;transition:none!important;caret-color:transparent}
      [data-backstory-focus]{outline:2px solid #6366f1!important;outline-offset:1px}
    `;
		head.appendChild(rw);
	}
	applyMutation(m) {
		if (!this.ready) return;
		if (m.removes) for (const r of m.removes) {
			const node = this.map.get(r.id);
			if (!node) continue;
			node.parentNode?.removeChild(node);
			this.unregisterTree(node);
		}
		if (m.adds) {
			let pending = [...m.adds];
			let progressed = true;
			while (pending.length && progressed) {
				progressed = false;
				const next = [];
				for (const a of pending) {
					const parent = this.map.get(a.parentId);
					if (!parent) {
						next.push(a);
						continue;
					}
					const isSvgParent = parent.namespaceURI === SVG_NS;
					const built = this.build(a.node, isSvgParent);
					if (!built) {
						progressed = true;
						continue;
					}
					const before = a.nextId != null ? this.map.get(a.nextId) : null;
					const host = this.attachTarget(parent);
					if (before && before.parentNode === host) host.insertBefore(built, before);
					else host.appendChild(built);
					progressed = true;
				}
				pending = next;
			}
		}
		if (m.attrs) for (const a of m.attrs) {
			const el = this.map.get(a.id);
			if (!el || el.nodeType !== 1) continue;
			const tag = el.tagName.toLowerCase();
			for (const [name, value] of Object.entries(a.attrs)) {
				if (value === null) {
					el.removeAttribute(name);
					continue;
				}
				const safe = sanitizeAttr(tag, name, value);
				if (safe === null) continue;
				try {
					el.setAttribute(name, this.finalAttr(tag, name, safe));
				} catch {}
			}
			if (el === this.doc.documentElement) {
				const explicit = a.attrs["data-backstory-replay-color-scheme"];
				if (explicit !== void 0) this.syncRecordedColorScheme(el, explicit);
				else if ("data-theme" in a.attrs || "class" in a.attrs || "style" in a.attrs) this.syncRecordedColorScheme(el, null);
			}
		}
		if (m.texts) for (const t of m.texts) {
			const node = this.map.get(t.id);
			if (!node) continue;
			if (node.nodeType === 3 || node.nodeType === 4 || node.nodeType === 8) node.textContent = node.parentNode?.nodeName?.toLowerCase() === "style" ? this.css(sanitizeCss(t.value)) : t.value;
			else if (node.tagName?.toLowerCase() === "style") node.textContent = this.css(sanitizeCss(t.value));
		}
	}
	/** Where children of `parent` are attached (shadow root if it has one). */
	attachTarget(parent) {
		const el = parent;
		if (el.nodeType === 1 && el.shadowRoot && el.getAttribute("data-backstory-shadow") === "") return el.shadowRoot;
		return parent;
	}
	applyInput(id, value, checked) {
		const el = this.map.get(id);
		if (!el || el.nodeType !== 1) return;
		const tag = el.tagName.toLowerCase();
		if (tag === "input" || tag === "textarea" || tag === "select") {
			if (typeof checked === "boolean" && "checked" in el) el.checked = checked;
			if ("value" in el) {
				el.value = value;
				if (tag !== "select") el.setAttribute("value", value);
			}
		} else if (el.isContentEditable || el.getAttribute("contenteditable") != null) el.textContent = value;
	}
	applyScroll(id, x, y) {
		if (id === 0) {
			const doc = this.doc;
			doc.documentElement.scrollLeft = x;
			doc.documentElement.scrollTop = y;
			if (doc.body) {
				doc.body.scrollLeft = x;
				doc.body.scrollTop = y;
			}
			return;
		}
		const el = this.map.get(id);
		if (!el || el.nodeType !== 1) return;
		el.scrollLeft = x;
		el.scrollTop = y;
	}
	applyFocus(id) {
		this.doc.querySelectorAll("[data-backstory-focus]").forEach((e) => e.removeAttribute("data-backstory-focus"));
		if (id == null) return;
		const el = this.map.get(id);
		if (el && el.nodeType === 1) el.setAttribute("data-backstory-focus", "");
	}
	/** All (id, node) pairs currently mapped; used by panels. */
	entries() {
		return this.map.entries();
	}
	/** Viewport-relative bounding box of a node, for pointer anchoring. */
	boxOf(id) {
		const el = this.map.get(id);
		if (!el || el.nodeType !== 1) return null;
		return el.getBoundingClientRect();
	}
	destroy() {
		this.map.clear();
		this.iframe.remove();
	}
	register(id, node) {
		this.map.set(id, node);
		this.reverse.set(node, id);
	}
	unregisterTree(node) {
		const id = this.reverse.get(node);
		if (id != null) this.map.delete(id);
		const el = node;
		if (el.nodeType === 1 && el.shadowRoot) for (const c of Array.from(el.shadowRoot.childNodes)) this.unregisterTree(c);
		for (const c of Array.from(node.childNodes)) this.unregisterTree(c);
	}
	build(sn, inSvg) {
		const doc = this.doc;
		switch (sn.type) {
			case "document": return null;
			case "doctype": return null;
			case "text": {
				const t = doc.createTextNode(sn.isStyle ? this.css(sanitizeCss(sn.text)) : sn.text);
				this.register(sn.id, t);
				return t;
			}
			case "cdata": {
				const t = doc.createTextNode(sn.text);
				this.register(sn.id, t);
				return t;
			}
			case "comment": {
				const c = doc.createComment(sn.text);
				this.register(sn.id, c);
				return c;
			}
			case "element": return this.buildElement(sn, inSvg);
		}
	}
	buildElement(sn, inSvg) {
		const doc = this.doc;
		const tag = sn.tag.toLowerCase();
		if (isBlockedTag(tag)) {
			const span = doc.createElement("template");
			span.setAttribute("data-backstory-removed", tag);
			this.register(sn.id, span);
			return span;
		}
		const svg = sn.isSVG || inSvg || tag === "svg";
		let el;
		try {
			el = svg ? doc.createElementNS(SVG_NS, sn.tag) : doc.createElement(tag);
		} catch {
			el = doc.createElement("div");
			el.setAttribute("data-backstory-invalid-tag", tag);
		}
		this.register(sn.id, el);
		this.applyAttrs(el, sn, tag);
		if (sn.blocked) {
			el.setAttribute("data-backstory-blocked", "");
			if (sn.box) {
				el.style.width = `${sn.box.w}px`;
				el.style.height = `${sn.box.h}px`;
			}
			return el;
		}
		if (PLACEHOLDER_TAGS.has(tag)) {
			const ph = doc.createElement("div");
			ph.setAttribute("data-backstory-placeholder", tag);
			for (const a of Array.from(el.attributes)) if (a.name === "class" || a.name === "id" || a.name === "style") ph.setAttribute(a.name, a.value);
			const w = sn.box?.w ?? (Number(sn.attrs["width"]) || 300);
			const h = sn.box?.h ?? (Number(sn.attrs["height"]) || 150);
			ph.style.width = `${w}px`;
			ph.style.height = `${h}px`;
			ph.style.lineHeight = `${h}px`;
			ph.textContent = tag === "canvas" ? "canvas" : tag;
			this.register(sn.id, ph);
			return ph;
		}
		if (tag === "style" || tag === "link" && sn.cssText != null) {
			const style = doc.createElement("style");
			if (sn.cssText != null) style.textContent = this.css(sanitizeCss(sn.cssText));
			for (const a of Array.from(el.attributes)) if (a.name !== "href" && a.name !== "rel") style.setAttribute(a.name, a.value);
			style.setAttribute("data-backstory-from", tag);
			this.register(sn.id, style);
			for (const c of sn.childNodes) {
				if (c.type === "text") {
					const t = doc.createTextNode(this.css(sanitizeCss(c.text)));
					this.register(c.id, t);
					style.appendChild(t);
					continue;
				}
				const built = this.build(c, false);
				if (built) style.appendChild(built);
			}
			return style;
		}
		if (sn.shadow) try {
			const root = el.attachShadow({ mode: "open" });
			el.setAttribute("data-backstory-shadow", "");
			for (const c of sn.shadow) {
				const built = this.build(c, false);
				if (built) root.appendChild(built);
			}
		} catch {}
		for (const c of sn.childNodes) {
			const built = this.build(c, svg);
			if (built) el.appendChild(built);
		}
		return el;
	}
	applyAttrs(el, sn, tag) {
		for (const [name, value] of Object.entries(sn.attrs)) {
			const safe = sanitizeAttr(tag, name, value);
			if (safe === null) continue;
			try {
				el.setAttribute(name, this.finalAttr(tag, name, safe));
			} catch {}
		}
		if (sn.masked) el.setAttribute("data-backstory-masked", "");
	}
	/**
	* Keep the sandbox canvas and native controls on the source page's scheme.
	* Older recordings may only carry data-theme/class/style, so infer those
	* before falling back to deterministic light instead of the agent's theme.
	*/
	syncRecordedColorScheme(html, recorded) {
		let scheme = recorded === "dark" || recorded === "light" ? recorded : null;
		const theme = html.getAttribute("data-theme")?.toLowerCase();
		if (!scheme && (theme === "dark" || theme === "light")) scheme = theme;
		if (!scheme && html.classList.contains("dark")) scheme = "dark";
		if (!scheme) {
			const style = html.getAttribute("style") ?? "";
			const match = /(?:^|;)\s*color-scheme\s*:\s*(dark|light)\b/i.exec(style);
			if (match) scheme = match[1].toLowerCase();
		}
		scheme ??= "light";
		this.iframe.style.colorScheme = scheme;
		this.iframe.style.background = scheme === "dark" ? "#111827" : "#fff";
		html.style.setProperty("color-scheme", scheme, "important");
	}
	/** Sanitized value → final value after CSS sanitizing and asset rewriting. */
	finalAttr(tag, name, safe) {
		const n = name.toLowerCase();
		if (n === "style") return this.css(sanitizeCss(safe));
		const rw = this.assetRewriter;
		if (rw && REWRITE_ATTRS.has(n)) {
			if (n === "srcset") return rewriteSrcset(safe, (u) => rw(this.absolute(u), {
				tag,
				attr: n
			}));
			if (tag === "a" && n === "href") return safe;
			return rw(this.absolute(safe), {
				tag,
				attr: n
			});
		}
		return safe;
	}
	/** CSS text → CSS text with archived asset URLs. */
	css(text) {
		const rw = this.assetRewriter;
		if (!rw) return text;
		return rewriteCssUrls(text, (u) => rw(this.absolute(u), {
			tag: "style",
			attr: "url"
		}));
	}
	absolute(url) {
		if (!this.baseUrl) return url;
		try {
			return new URL(url, this.baseUrl).toString();
		} catch {
			return url;
		}
	}
};
//#endregion
//#region src/panels/dom.ts
/** DOM helpers shared by the Elements and Accessibility panels and the inspect overlay. */
var HASHED = /(^|[_-])[a-f0-9]{6,}($|[_-])|^[A-Za-z0-9_-]{20,}$|[A-Za-z]+[0-9]{5,}/;
function isHashedToken(s) {
	return HASHED.test(s);
}
/** Short human label: tag#id.class (skips hashed classes/ids). */
function describeElement(el) {
	const tag = el.tagName.toLowerCase();
	const id = el.getAttribute("id");
	const testId = el.getAttribute("data-testid");
	let s = tag;
	if (testId) s += `[data-testid=${testId}]`;
	else if (id && !isHashedToken(id)) s += `#${id}`;
	const classes = Array.from(el.classList).filter((c) => !isHashedToken(c)).slice(0, 2);
	if (classes.length) s += `.${classes.join(".")}`;
	return s;
}
/**
* Masked-safe, stable selector: prefers data-testid, then non-hashed id, then
* role/name attributes, then a tag path with nth-of-type. Never includes text.
*/
function selectorFor(el, maxDepth = 6) {
	const parts = [];
	let cur = el;
	let depth = 0;
	while (cur && cur.nodeType === 1 && depth < maxDepth) {
		const tag = cur.tagName.toLowerCase();
		if (tag === "html" || tag === "body") {
			parts.unshift(tag);
			break;
		}
		const testId = cur.getAttribute("data-testid") ?? cur.getAttribute("data-test") ?? cur.getAttribute("data-cy");
		if (testId) {
			parts.unshift(`${tag}[data-testid="${cssEscape(testId)}"]`);
			break;
		}
		const id = cur.getAttribute("id");
		if (id && !isHashedToken(id)) {
			parts.unshift(`#${cssEscape(id)}`);
			break;
		}
		const role = cur.getAttribute("role");
		const name = cur.getAttribute("name");
		let seg = tag;
		if (name && !isHashedToken(name)) seg += `[name="${cssEscape(name)}"]`;
		else if (role) seg += `[role="${cssEscape(role)}"]`;
		else {
			const stable = Array.from(cur.classList).filter((c) => !isHashedToken(c)).slice(0, 2);
			if (stable.length) seg += `.${stable.map(cssEscape).join(".")}`;
		}
		const parent = cur.parentElement;
		if (parent) {
			const same = Array.from(parent.children).filter((c) => c.tagName === cur.tagName);
			if (same.length > 1) seg += `:nth-of-type(${same.indexOf(cur) + 1})`;
		}
		parts.unshift(seg);
		cur = parent;
		depth++;
	}
	return parts.join(" > ");
}
function xpathFor(el) {
	const parts = [];
	let cur = el;
	while (cur && cur.nodeType === 1) {
		const tag = cur.tagName.toLowerCase();
		const parent = cur.parentElement;
		if (!parent) {
			parts.unshift(`/${tag}`);
			break;
		}
		const same = Array.from(parent.children).filter((c) => c.tagName === cur.tagName);
		parts.unshift(same.length > 1 ? `${tag}[${same.indexOf(cur) + 1}]` : tag);
		cur = parent;
	}
	return "/" + parts.join("/").replace(/^\/+/, "");
}
function cssEscape(s) {
	return s.replace(/["\\]/g, "\\$&");
}
var px = (v) => Number.parseFloat(v) || 0;
function boxModelOf(el) {
	const win = el.ownerDocument.defaultView;
	if (!win) return null;
	const cs = win.getComputedStyle(el);
	const r = el.getBoundingClientRect();
	const pad = [
		px(cs.paddingTop),
		px(cs.paddingRight),
		px(cs.paddingBottom),
		px(cs.paddingLeft)
	];
	const bor = [
		px(cs.borderTopWidth),
		px(cs.borderRightWidth),
		px(cs.borderBottomWidth),
		px(cs.borderLeftWidth)
	];
	const mar = [
		px(cs.marginTop),
		px(cs.marginRight),
		px(cs.marginBottom),
		px(cs.marginLeft)
	];
	return {
		content: {
			w: Math.max(0, r.width - pad[1] - pad[3] - bor[1] - bor[3]),
			h: Math.max(0, r.height - pad[0] - pad[2] - bor[0] - bor[2])
		},
		padding: pad,
		border: bor,
		margin: mar,
		rect: {
			x: r.left,
			y: r.top,
			w: r.width,
			h: r.height
		}
	};
}
var COMMON_STYLE_KEYS = [
	"display",
	"position",
	"width",
	"height",
	"margin",
	"padding",
	"color",
	"background-color",
	"font-family",
	"font-size",
	"font-weight",
	"line-height",
	"border",
	"border-radius",
	"opacity",
	"visibility",
	"overflow",
	"z-index",
	"flex",
	"grid-template-columns",
	"cursor"
];
function computedStylesOf(el, keys = COMMON_STYLE_KEYS) {
	const win = el.ownerDocument.defaultView;
	if (!win) return [];
	const cs = win.getComputedStyle(el);
	const out = [];
	for (const k of keys) {
		const v = cs.getPropertyValue(k);
		if (v) out.push([k, v]);
	}
	return out;
}
function attributesOf(el) {
	return Array.from(el.attributes).filter((a) => !a.name.startsWith("data-backstory-")).map((a) => [a.name, a.value]);
}
function ancestorsOf(el) {
	const out = [];
	let cur = el.parentElement;
	while (cur) {
		out.unshift(cur);
		cur = cur.parentElement;
	}
	return out;
}
/** Accessible name per a simplified accname algorithm. */
function accessibleName(el) {
	const labelledBy = el.getAttribute("aria-labelledby");
	if (labelledBy) {
		const doc = el.ownerDocument;
		const txt = labelledBy.split(/\s+/).map((id) => doc.getElementById(id)?.textContent?.trim() ?? "").join(" ").trim();
		if (txt) return txt;
	}
	const ariaLabel = el.getAttribute("aria-label");
	if (ariaLabel?.trim()) return ariaLabel.trim();
	const tag = el.tagName.toLowerCase();
	if (tag === "img" || tag === "area") return (el.getAttribute("alt") ?? "").trim();
	if (tag === "input") {
		const type = (el.getAttribute("type") ?? "text").toLowerCase();
		if (type === "submit" || type === "button" || type === "reset") return (el.getAttribute("value") ?? "").trim();
		if (type === "image") return (el.getAttribute("alt") ?? "").trim();
	}
	if (tag === "input" || tag === "textarea" || tag === "select") {
		const id = el.getAttribute("id");
		if (id) {
			const label = el.ownerDocument.querySelector(`label[for="${cssEscape(id)}"]`);
			if (label?.textContent?.trim()) return label.textContent.trim();
		}
		const wrapping = el.closest("label");
		if (wrapping?.textContent?.trim()) return wrapping.textContent.trim();
		const ph = el.getAttribute("placeholder");
		if (ph?.trim()) return ph.trim();
		const title = el.getAttribute("title");
		if (title?.trim()) return title.trim();
		return "";
	}
	const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
	if (text) return text;
	const imgAlt = el.querySelector("img[alt]")?.getAttribute("alt")?.trim();
	if (imgAlt) return imgAlt;
	return (el.getAttribute("title") ?? "").trim();
}
//#endregion
//#region src/timeline.ts
/**
* Timeline holds every loaded event sorted by time plus indexes the Player
* needs for seeking. Chunks may be added out of order; events are re-sorted
* cheaply because chunks are internally ordered.
*/
var Timeline = class {
	events = [];
	/** Indexes into `events` where a snapshot event lives, ascending. */
	snapshots = [];
	gaps = [];
	loadedSeqs = /* @__PURE__ */ new Set();
	refs = [];
	/** Latest session metadata seen (chunk `meta`), by lowest seq first then updates. */
	meta;
	metaSeq = -1;
	startTs = Number.POSITIVE_INFINITY;
	endTs = Number.NEGATIVE_INFINITY;
	setManifest(refs) {
		this.refs = [...refs].sort((a, b) => a.seq - b.seq);
		for (const r of this.refs) {
			this.startTs = Math.min(this.startTs, r.firstTs);
			this.endTs = Math.max(this.endTs, r.lastTs);
		}
	}
	get manifest() {
		return this.refs;
	}
	hasChunk(seq) {
		return this.loadedSeqs.has(seq);
	}
	get duration() {
		if (!Number.isFinite(this.startTs) || !Number.isFinite(this.endTs)) return 0;
		return Math.max(0, this.endTs - this.startTs);
	}
	addChunk(chunk) {
		if (this.loadedSeqs.has(chunk.seq)) return;
		this.loadedSeqs.add(chunk.seq);
		if (chunk.meta && chunk.seq > this.metaSeq) {
			this.meta = {
				...this.meta ?? {},
				...chunk.meta
			};
			this.metaSeq = chunk.seq;
		}
		const incoming = chunk.events.map((event, idx) => ({
			t: event.t,
			seq: chunk.seq,
			idx,
			event
		}));
		if (incoming.length) {
			this.startTs = Math.min(this.startTs, incoming[0].t, chunk.firstTs);
			this.endTs = Math.max(this.endTs, incoming[incoming.length - 1].t, chunk.lastTs);
		} else {
			this.startTs = Math.min(this.startTs, chunk.firstTs);
			this.endTs = Math.max(this.endTs, chunk.lastTs);
		}
		this.events = mergeSorted(this.events, incoming);
		this.rebuildIndexes();
		for (const g of chunk.gaps ?? []) {
			const end = chunk.firstTs;
			this.pushGap({
				start: end - g.ms,
				end,
				reason: g.reason,
				fromSeq: g.fromSeq,
				toSeq: g.toSeq
			});
		}
		for (const e of chunk.events) if (e.type === "control" && e.kind === "gap") this.pushGap({
			start: e.t - e.ms,
			end: e.t,
			reason: e.reason,
			fromSeq: e.fromSeq,
			toSeq: e.toSeq
		});
	}
	pushGap(g) {
		if (this.gaps.some((x) => x.fromSeq === g.fromSeq && x.toSeq === g.toSeq)) return;
		this.gaps.push(g);
		this.gaps.sort((a, b) => a.start - b.start);
	}
	rebuildIndexes() {
		this.snapshots = [];
		for (let i = 0; i < this.events.length; i++) if (this.events[i].event.type === "snapshot") this.snapshots.push(i);
	}
	/** Index of the last event with t <= time, or -1. */
	indexAt(time) {
		let lo = 0;
		let hi = this.events.length - 1;
		let ans = -1;
		while (lo <= hi) {
			const mid = lo + hi >> 1;
			if (this.events[mid].t <= time) {
				ans = mid;
				lo = mid + 1;
			} else hi = mid - 1;
		}
		return ans;
	}
	/** Index (into events) of the snapshot to rebuild from for `time`. */
	snapshotIndexFor(time) {
		let best = -1;
		for (const si of this.snapshots) if (this.events[si].t <= time) best = si;
		else break;
		if (best === -1 && this.snapshots.length) best = this.snapshots[0];
		return best;
	}
	/** The chunk seq whose [firstTs,lastTs] contains `time`, from the manifest or loaded chunks. */
	seqAt(time) {
		for (const r of this.refs) if (time >= r.firstTs && time <= r.lastTs) return r.seq;
		for (const r of this.refs) if (r.firstTs > time) return r.seq;
		return this.refs.length ? this.refs[this.refs.length - 1].seq : void 0;
	}
	/** First seq that carries the checkpoint the given seq builds on. */
	checkpointStartSeq(seq) {
		const ref = this.refs.find((r) => r.seq === seq);
		if (!ref) return seq;
		let start = seq;
		for (const r of this.refs) if (r.checkpointId === ref.checkpointId) {
			start = r.seq;
			break;
		}
		return start;
	}
	/** Periods with no events for longer than `thresholdMs`, from loaded events and manifest boundaries. */
	inactivity(thresholdMs) {
		const spans = [];
		const times = [];
		for (const e of this.events) times.push(e.t);
		for (const r of this.refs) times.push(r.firstTs, r.lastTs);
		times.sort((a, b) => a - b);
		for (let i = 1; i < times.length; i++) {
			const a = times[i - 1];
			const b = times[i];
			if (b - a > thresholdMs) spans.push({
				start: a,
				end: b
			});
		}
		return mergeSpans(spans);
	}
	gapAt(time) {
		return this.gaps.find((g) => time >= g.start && time <= g.end);
	}
};
function mergeSorted(a, b) {
	if (!a.length) return b;
	if (!b.length) return a;
	const out = new Array(a.length + b.length);
	let i = 0;
	let j = 0;
	let k = 0;
	while (i < a.length && j < b.length) {
		const x = a[i];
		const y = b[j];
		if (x.t < y.t || x.t === y.t && (x.seq < y.seq || x.seq === y.seq && x.idx < y.idx)) out[k++] = a[i++];
		else out[k++] = b[j++];
	}
	while (i < a.length) out[k++] = a[i++];
	while (j < b.length) out[k++] = b[j++];
	return out;
}
function mergeSpans(spans) {
	if (!spans.length) return spans;
	spans.sort((x, y) => x.start - y.start);
	const out = [spans[0]];
	for (let i = 1; i < spans.length; i++) {
		const last = out[out.length - 1];
		const s = spans[i];
		if (s.start <= last.end) last.end = Math.max(last.end, s.end);
		else out.push(s);
	}
	return out;
}
//#endregion
//#region src/player.ts
var VISIBLE_STEP_TYPES = /* @__PURE__ */ new Set([
	"snapshot",
	"mutation",
	"pointer",
	"scroll",
	"input",
	"navigation",
	"viewport"
]);
/**
* Player drives a Mirror through time. Times exposed publicly are relative
* milliseconds from the start of the session; internally we use absolute epoch ms.
*/
var Player = class {
	container;
	timeline = new Timeline();
	mirror;
	cursor;
	highlight;
	/** Time-range selection ("Explain this moment"); relative ms. */
	selection = new SelectionModel();
	/** Canvas playback tracks (command re-execution and video decode), PLAN §6.6. */
	canvasTracks;
	wrapper;
	stage;
	urlBar;
	gapOverlay;
	opts;
	listeners = /* @__PURE__ */ new Map();
	_state = "idle";
	_speed;
	currentAbsMs = 0;
	appliedKey = null;
	appliedSnapshotKey = null;
	fetcher = null;
	inflight = /* @__PURE__ */ new Map();
	rafHandle = null;
	lastTick = 0;
	viewport = {
		w: 1024,
		h: 768
	};
	scheduledPointer = null;
	inactivityCache = null;
	version = 0;
	currentGap = null;
	resizeObs = null;
	destroyed = false;
	hydrating = false;
	/** Whether every manifest chunk is loaded, so the panels cover the whole session. */
	hydratedFully = false;
	constructor(container, options = {}) {
		this.container = container;
		this.opts = {
			baseUrl: options.baseUrl,
			speed: options.speed ?? 1,
			skipInactivity: options.skipInactivity ?? true,
			inactivityThresholdMs: options.inactivityThresholdMs ?? 5e3,
			fit: options.fit ?? true,
			allowUpscale: options.allowUpscale ?? false,
			showUrlBar: options.showUrlBar ?? true,
			prefetchMs: options.prefetchMs ?? 1e4,
			hidePhi: options.hidePhi ?? false
		};
		this._speed = this.opts.speed;
		if (!container.style.position) {
			if ((typeof getComputedStyle === "function" ? getComputedStyle(container).position : "static") === "static") container.style.position = "relative";
		}
		container.style.overflow = "hidden";
		this.wrapper = document.createElement("div");
		this.wrapper.setAttribute("data-backstory-wrapper", "");
		Object.assign(this.wrapper.style, {
			position: "absolute",
			left: "0",
			top: "0",
			transformOrigin: "0 0"
		});
		this.stage = document.createElement("div");
		this.stage.setAttribute("data-backstory-stage", "");
		Object.assign(this.stage.style, {
			position: "relative",
			width: "1024px",
			height: "768px"
		});
		this.wrapper.appendChild(this.stage);
		container.appendChild(this.wrapper);
		this.mirror = new Mirror(this.stage, {
			baseUrl: options.baseUrl,
			assetRewriter: options.assetRewriter
		});
		this.cursor = new CursorOverlay(this.stage);
		this.highlight = new HighlightOverlay(this.stage);
		this.canvasTracks = new CanvasTracks(this.mirror, { hidePhi: options.hidePhi });
		this.selection.onChange((range) => this.emit("selectionchange", { range }));
		this.urlBar = document.createElement("div");
		this.urlBar.setAttribute("data-backstory-urlbar", "");
		Object.assign(this.urlBar.style, {
			position: "absolute",
			left: "8px",
			top: "8px",
			maxWidth: "70%",
			padding: "2px 8px",
			borderRadius: "999px",
			background: "rgba(17,24,39,.75)",
			color: "#fff",
			font: "11px/16px system-ui, sans-serif",
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
			pointerEvents: "none",
			display: "none"
		});
		container.appendChild(this.urlBar);
		this.gapOverlay = document.createElement("div");
		this.gapOverlay.setAttribute("data-backstory-gap", "");
		Object.assign(this.gapOverlay.style, {
			position: "absolute",
			inset: "0",
			display: "none",
			alignItems: "center",
			justifyContent: "center",
			background: "repeating-linear-gradient(45deg, rgba(0,0,0,.06) 0 10px, rgba(0,0,0,.12) 10px 20px)",
			color: "#111827",
			font: "13px system-ui, sans-serif",
			pointerEvents: "none"
		});
		const badge = document.createElement("div");
		badge.setAttribute("data-backstory-gap-text", "");
		Object.assign(badge.style, {
			background: "rgba(255,255,255,.95)",
			border: "1px solid #d1d5db",
			borderRadius: "6px",
			padding: "6px 10px"
		});
		this.gapOverlay.appendChild(badge);
		container.appendChild(this.gapOverlay);
		this.setViewportSize(this.viewport.w, this.viewport.h);
		if (typeof ResizeObserver !== "undefined") {
			this.resizeObs = new ResizeObserver(() => this.fit());
			this.resizeObs.observe(container);
			if (container.parentElement) this.resizeObs.observe(container.parentElement);
		}
		if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => this.fit());
	}
	on(type, fn) {
		let set = this.listeners.get(type);
		if (!set) {
			set = /* @__PURE__ */ new Set();
			this.listeners.set(type, set);
		}
		set.add(fn);
		return () => set.delete(fn);
	}
	emit(type, payload) {
		const set = this.listeners.get(type);
		if (!set) return;
		for (const fn of set) try {
			fn(payload);
		} catch (error) {
			if (type !== "error") this.emit("error", { error });
		}
	}
	/** Discard loaded data so a new session/segment can be loaded into the same Player. */
	resetTimeline() {
		this.cancelTick();
		this.timeline = new Timeline();
		this.inflight.clear();
		this.hydratedFully = false;
		this.appliedKey = null;
		this.appliedSnapshotKey = null;
		this.scheduledPointer = null;
		this.inactivityCache = null;
		this.currentGap = null;
		this.gapOverlay.style.display = "none";
		this.cursor.hide();
		this.cursor.clearRipples();
		this.version++;
	}
	/** Load a complete session held in memory. Replaces any previously loaded session. */
	loadChunks(chunks) {
		this.resetTimeline();
		this.fetcher = null;
		const sorted = [...chunks].sort((a, b) => a.seq - b.seq);
		this.timeline.setManifest(sorted.map((c) => ({
			seq: c.seq,
			firstTs: c.firstTs,
			lastTs: c.lastTs,
			size: 0,
			checkpointId: c.checkpointId
		})));
		for (const c of sorted) {
			this.timeline.addChunk(c);
			this.emit("chunkloaded", { seq: c.seq });
		}
		this.hydratedFully = true;
		this.version++;
		this.afterLoad();
	}
	/** Load lazily from a manifest; chunks are fetched around the playhead. */
	async loadManifest(refs, fetcher) {
		this.resetTimeline();
		this.fetcher = fetcher;
		this.timeline.setManifest(refs);
		this.setState("loading");
		const first = this.timeline.manifest[0];
		if (first) await this.ensureChunk(first.seq);
		if (!this.renderable) await this.loadOpeningSnapshot();
		this.afterLoad();
	}
	/**
	* Fetch every chunk the manifest lists, oldest first, so the panels describe
	* the whole session instead of the few seconds around the playhead. Playback
	* only needs chunks near the cursor, but Network, Console and Issues are read
	* as a complete record of the visit: a request that has not been fetched yet
	* is indistinguishable from one that never happened.
	*
	* Fetches run one at a time and yield between chunks so a seek still wins the
	* race for the network, and the byte budget keeps a long recording from
	* pulling its entire history into memory.
	*/
	async hydrateAll(maxBytes = 25165824) {
		if (!this.fetcher || this.hydrating) return;
		this.hydrating = true;
		try {
			let bytes = 0;
			for (const r of this.timeline.manifest) {
				if (this.destroyed || !this.fetcher) return;
				if (this.timeline.hasChunk(r.seq)) continue;
				bytes += r.size;
				if (bytes > maxBytes) {
					this.hydratedFully = false;
					return;
				}
				try {
					await this.ensureChunk(r.seq);
				} catch {}
				await new Promise((resolve) => setTimeout(resolve, 0));
			}
			this.hydratedFully = true;
		} finally {
			this.hydrating = false;
			this.emit("hydrated", { complete: this.hydratedFully });
		}
	}
	/**
	* Find the opening snapshot when the session's first chunk does not carry
	* one. A recorder can flush an early event — a web vital, the surface's
	* visibility — before the snapshot is ready, which leaves the picture in a
	* later chunk. Only a chunk that opens a checkpoint can hold a snapshot, so
	* those are tried first; a few of their neighbours follow for recordings made
	* before checkpoints were tracked.
	*/
	async loadOpeningSnapshot() {
		const refs = this.timeline.manifest;
		let checkpoint = refs[0]?.checkpointId;
		let probes = 8;
		let neighbours = 2;
		for (const r of refs.slice(1)) {
			if (this.destroyed || this.renderable || probes <= 0) return;
			const opensCheckpoint = r.checkpointId !== checkpoint;
			checkpoint = r.checkpointId;
			if (!opensCheckpoint && neighbours-- <= 0) continue;
			probes--;
			await this.ensureChunk(r.seq);
		}
	}
	afterLoad() {
		this.currentAbsMs = this.timeline.startTs;
		this.appliedKey = null;
		this.appliedSnapshotKey = null;
		this.selection.durationMs = this.timeline.duration;
		this.seekAbs(this.timeline.startTs).then(() => {
			this.setState(this.renderable ? "paused" : "unavailable");
			this.emit("loaded", {
				duration: this.timeline.duration,
				chunks: this.timeline.manifest.length
			});
		});
	}
	/**
	* Whether any loaded chunk carries a full snapshot. Everything the mirror
	* renders is built from one, so without it playback has no picture no matter
	* how many mutation or pointer events arrived.
	*/
	get renderable() {
		return this.timeline.snapshots.length > 0;
	}
	async ensureChunk(seq) {
		if (this.destroyed || this.timeline.hasChunk(seq) || !this.fetcher) return;
		let p = this.inflight.get(seq);
		if (!p) {
			p = this.fetcher(seq);
			this.inflight.set(seq, p);
		}
		try {
			const chunk = await p;
			this.timeline.addChunk(chunk);
			this.version++;
			if (this._state === "unavailable" && this.renderable) {
				this.setState("paused");
				this.appliedKey = null;
				this.appliedSnapshotKey = null;
				await this.seekAbs(this.currentAbsMs);
			}
			this.emit("chunkloaded", { seq });
		} finally {
			this.inflight.delete(seq);
		}
	}
	/** Make sure every chunk needed to render `absTime` is loaded (checkpoint start → containing chunk). */
	async ensureLoadedUpTo(absTime) {
		if (!this.fetcher) return;
		const seq = this.timeline.seqAt(absTime);
		if (seq == null) return;
		const start = this.timeline.checkpointStartSeq(seq);
		const refs = this.timeline.manifest.filter((r) => r.seq >= start && r.seq <= seq);
		for (const r of refs) {
			if (this.destroyed) return;
			await this.ensureChunk(r.seq);
		}
	}
	prefetch(absTime) {
		if (!this.fetcher) return;
		for (const r of this.timeline.manifest) if (!this.timeline.hasChunk(r.seq) && r.firstTs <= absTime + this.opts.prefetchMs && r.lastTs >= absTime - 1) {
			this.ensureChunk(r.seq).catch((error) => this.emit("error", { error }));
			break;
		}
	}
	get state() {
		return this._state;
	}
	setState(s) {
		if (this._state === s) return;
		this._state = s;
		this.emit("statechange", { state: s });
	}
	get speed() {
		return this._speed;
	}
	setSpeed(s) {
		this._speed = Math.max(.1, Math.min(16, s));
	}
	get skipInactivity() {
		return this.opts.skipInactivity;
	}
	setSkipInactivity(v) {
		this.opts.skipInactivity = v;
	}
	/** Milliseconds since session start. */
	get currentTime() {
		return Math.max(0, this.currentAbsMs - this.timeline.startTs);
	}
	get duration() {
		return this.timeline.duration;
	}
	get startTs() {
		return this.timeline.startTs;
	}
	get gaps() {
		return this.timeline.gaps;
	}
	get viewportSize() {
		return { ...this.viewport };
	}
	/** Latest session metadata carried by loaded chunks (seq 1 and identity changes). */
	get meta() {
		return this.timeline.meta;
	}
	/** Absolute epoch ms of the playhead. */
	get currentAbs() {
		return this.currentAbsMs;
	}
	/** Replace the asset rewriter (archived stylesheets, images, fonts); applies from the next rebuild. */
	setAssetRewriter(rewriter) {
		this.mirror.assetRewriter = rewriter;
		this.appliedKey = null;
		this.appliedSnapshotKey = null;
		this.applyUpTo(this.currentAbsMs, true);
	}
	/** Bounding box of a mirrored node in stage coordinates, or null. */
	boxOf(id) {
		const r = this.mirror.boxOf(id);
		if (!r) return null;
		return {
			x: r.left,
			y: r.top,
			w: r.width,
			h: r.height
		};
	}
	/** Highlight a node for `ms` (default 2000; 0 = until `clearHighlight`). */
	highlightNode(id, opts = {}) {
		if (id == null) {
			this.highlight.clear();
			return;
		}
		const el = this.mirror.getNode(id);
		const box = this.boxOf(id);
		if (!box) return;
		this.highlight.show([box], {
			ms: opts.ms ?? 2e3,
			color: opts.color,
			label: opts.label ?? (el && el.nodeType === 1 ? describeElement(el) : void 0)
		});
	}
	/** Draw arbitrary boxes (viewport coordinates), e.g. CLS shift sources. */
	drawBoxes(boxes, opts = {}) {
		this.highlight.show(boxes, {
			ms: opts.ms ?? 2e3,
			color: opts.color ?? "rgba(245,158,11,.9)",
			label: opts.label
		});
	}
	clearHighlight() {
		this.highlight.clear();
	}
	inspecting = false;
	get inspectMode() {
		return this.inspecting;
	}
	/** Inspect mode: hovering highlights the mirrored element, clicking emits `inspect` with picked=true. */
	setInspect(enabled) {
		this.inspecting = enabled;
		const resolve = (x, y) => {
			const doc = this.mirror.iframe.contentDocument;
			if (!doc) return {
				id: null,
				el: null
			};
			const el = doc.elementFromPoint(x, y);
			if (!el) return {
				id: null,
				el: null
			};
			return {
				id: this.mirror.getId(el) ?? null,
				el
			};
		};
		this.highlight.setInspect(enabled, (x, y) => {
			const { id, el } = resolve(x, y);
			if (id != null && el) {
				const b = this.boxOf(id);
				if (b) this.highlight.show([b], { label: describeElement(el) });
				this.emit("inspect", {
					nodeId: id,
					selector: selectorFor(el),
					picked: false
				});
			}
		}, (x, y) => {
			const { id, el } = resolve(x, y);
			this.emit("inspect", {
				nodeId: id,
				selector: el ? selectorFor(el) : null,
				picked: true
			});
		});
	}
	inactivitySpans() {
		if (!this.inactivityCache || this.inactivityCache.version !== this.version) this.inactivityCache = {
			spans: this.timeline.inactivity(this.opts.inactivityThresholdMs),
			version: this.version
		};
		return this.inactivityCache.spans;
	}
	play() {
		if (this.destroyed || this._state === "unavailable") return;
		if (this._state === "ended") {
			this.seek(0).then(() => this.play());
			return;
		}
		if (this._state === "playing") return;
		this.setState("playing");
		this.lastTick = nowMs();
		this.scheduleTick();
	}
	pause() {
		if (this._state !== "playing" && this._state !== "buffering") return;
		this.cancelTick();
		this.setState("paused");
	}
	toggle() {
		if (this._state === "playing") this.pause();
		else this.play();
	}
	/** Seek to a relative time in ms. */
	async seek(relMs) {
		const abs = this.timeline.startTs + Math.max(0, Math.min(this.timeline.duration, relMs));
		await this.seekAbs(abs);
		this.emit("seeked", { currentTime: this.currentTime });
	}
	/** Jump to the next/previous visible event. */
	async step(direction) {
		this.pause();
		const events = this.timeline.events;
		let i = this.timeline.indexAt(this.currentAbsMs);
		if (direction > 0) {
			for (let j = i + 1; j < events.length; j++) if (VISIBLE_STEP_TYPES.has(events[j].event.type)) {
				await this.seekAbs(events[j].t);
				this.emit("seeked", { currentTime: this.currentTime });
				return;
			}
			await this.seekAbs(this.timeline.endTs);
		} else {
			while (i >= 0 && events[i].t >= this.currentAbsMs) i--;
			for (let j = i; j >= 0; j--) if (VISIBLE_STEP_TYPES.has(events[j].event.type)) {
				await this.seekAbs(events[j].t);
				this.emit("seeked", { currentTime: this.currentTime });
				return;
			}
			await this.seekAbs(this.timeline.startTs);
		}
		this.emit("seeked", { currentTime: this.currentTime });
	}
	scheduleTick() {
		const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : null;
		this.rafHandle = raf ? raf(() => void this.tick()) : setTimeout(() => void this.tick(), 16);
	}
	cancelTick() {
		if (this.rafHandle == null) return;
		if (typeof cancelAnimationFrame === "function" && typeof this.rafHandle === "number") cancelAnimationFrame(this.rafHandle);
		else clearTimeout(this.rafHandle);
		this.rafHandle = null;
	}
	async tick() {
		if (this.destroyed || this._state !== "playing" && this._state !== "buffering") return;
		const now = nowMs();
		const dt = (now - this.lastTick) * this._speed;
		this.lastTick = now;
		let target = this.currentAbsMs + dt;
		if (this.opts.skipInactivity) {
			for (const span of this.inactivitySpans()) if (target > span.start + 300 && target < span.end) {
				target = span.end - 50;
				this.cursor.clearRipples();
				break;
			}
		}
		const needSeq = this.timeline.seqAt(target);
		if (needSeq != null && !this.timeline.hasChunk(needSeq) && this.fetcher) {
			this.setState("buffering");
			try {
				await this.ensureLoadedUpTo(target);
			} catch (error) {
				this.emit("error", { error });
				this.pause();
				return;
			}
			if (this._state === "buffering") this.setState("playing");
			this.lastTick = nowMs();
		}
		if (target >= this.timeline.endTs) {
			this.applyUpTo(this.timeline.endTs, false);
			this.currentAbsMs = this.timeline.endTs;
			this.cursor.render(this.currentAbsMs);
			this.updateGap();
			this.emit("timeupdate", {
				currentTime: this.currentTime,
				duration: this.duration
			});
			this.cancelTick();
			this.setState("ended");
			return;
		}
		this.applyUpTo(target, false);
		this.currentAbsMs = target;
		this.schedulePointerLookahead();
		this.cursor.render(this.currentAbsMs);
		this.updateGap();
		this.prefetch(target);
		this.emit("timeupdate", {
			currentTime: this.currentTime,
			duration: this.duration
		});
		this.scheduleTick();
	}
	keyIndex(key) {
		if (!key) return -1;
		const events = this.timeline.events;
		for (let i = 0; i < events.length; i++) {
			const e = events[i];
			if (e.seq === key.seq && e.idx === key.idx) return i;
		}
		return -1;
	}
	async seekAbs(absTime) {
		const wasPlaying = this._state === "playing";
		this.cancelTick();
		try {
			await this.ensureLoadedUpTo(absTime);
		} catch (error) {
			this.emit("error", { error });
		}
		this.cursor.clearRipples();
		this.applyUpTo(absTime, true);
		this.canvasTracks.flush(absTime);
		this.currentAbsMs = absTime;
		this.scheduledPointer = null;
		this.cursor.render(absTime);
		this.updateGap();
		this.emit("timeupdate", {
			currentTime: this.currentTime,
			duration: this.duration
		});
		if (wasPlaying) {
			this.lastTick = nowMs();
			this.setState("playing");
			this.scheduleTick();
		} else if (this._state === "ended" && absTime < this.timeline.endTs) this.setState("paused");
	}
	/** Bring the mirror to the state at `absTime`, rebuilding from a snapshot when needed. */
	applyUpTo(absTime, seeking) {
		const tl = this.timeline;
		if (!tl.events.length) return;
		const targetIdx = tl.indexAt(absTime);
		const snapIdx = tl.snapshotIndexFor(absTime);
		if (snapIdx === -1) return;
		const snapKey = {
			seq: tl.events[snapIdx].seq,
			idx: tl.events[snapIdx].idx
		};
		const appliedIdx = this.keyIndex(this.appliedKey);
		const sameSnapshot = this.appliedSnapshotKey && this.appliedSnapshotKey.seq === snapKey.seq && this.appliedSnapshotKey.idx === snapKey.idx;
		let from;
		if (sameSnapshot && appliedIdx !== -1 && appliedIdx <= targetIdx && appliedIdx >= snapIdx) from = appliedIdx + 1;
		else {
			this.applyOne(tl.events[snapIdx], true);
			this.appliedSnapshotKey = snapKey;
			this.appliedKey = snapKey;
			from = snapIdx + 1;
			this.cursor.hide();
		}
		for (let i = from; i <= targetIdx; i++) {
			const te = tl.events[i];
			this.applyOne(te, seeking);
			this.appliedKey = {
				seq: te.seq,
				idx: te.idx
			};
		}
	}
	applyOne(te, seeking) {
		const e = te.event;
		try {
			switch (e.type) {
				case "snapshot":
					this.applySnapshot(e);
					break;
				case "mutation":
					this.mirror.applyMutation(e);
					break;
				case "pointer":
					if (e.kind === "move" || e.kind === "touchmove") {
						if (e.actor) this.cursor.set(e.x, e.y, e.actor);
						else if (seeking) {
							const last = e.path && e.path.length ? e.path[e.path.length - 1] : null;
							this.cursor.set(last ? last.x : e.x, last ? last.y : e.y);
						} else if (e.path && e.path.length) this.cursor.movePath([{
							x: e.x,
							y: e.y,
							t: te.t
						}, ...e.path.map((p) => ({
							x: p.x,
							y: p.y,
							t: te.t + p.dt
						}))], this.currentAbsMs);
						else this.cursor.moveTo(e.x, e.y, this.currentAbsMs, te.t);
					} else {
						this.cursor.set(e.x, e.y, e.actor);
						if (!seeking && (e.kind === "click" || e.kind === "down" || e.kind === "touchstart" || e.kind === "dblclick")) this.cursor.ripple(e.x, e.y, e.actor ? "rgba(245,158,11,.6)" : void 0);
					}
					break;
				case "scroll":
					this.mirror.applyScroll(e.id, e.x, e.y);
					break;
				case "input":
					this.mirror.applyInput(e.id, e.value, e.checked);
					break;
				case "focus":
					this.mirror.applyFocus(e.id);
					break;
				case "blur":
					this.mirror.applyFocus(null);
					break;
				case "viewport":
					this.setViewportSize(e.w, e.h);
					break;
				case "navigation":
					this.applyNavigation(e);
					break;
				case "canvas_cmds":
				case "canvas_frame": this.canvasTracks.apply(e, te.t, seeking);
			}
		} catch (error) {
			this.emit("error", { error });
		}
		this.emit("event", {
			event: e,
			t: te.t,
			seq: te.seq,
			idx: te.idx,
			seeking
		});
	}
	applySnapshot(snap) {
		this.setViewportSize(snap.viewport.w, snap.viewport.h);
		this.canvasTracks.reset();
		this.mirror.applySnapshot(snap);
		this.showUrl(snap.url, snap.title);
	}
	applyNavigation(nav) {
		this.showUrl(nav.url, nav.title);
		this.emit("navigation", {
			url: nav.url,
			title: nav.title
		});
	}
	showUrl(url, title) {
		this.urlBar.textContent = title ? `${title} — ${url}` : url;
		this.urlBar.style.display = this.opts.showUrlBar && url ? "block" : "none";
	}
	schedulePointerLookahead() {
		if (this.cursor.pendingWaypoints(this.currentAbsMs).length) return;
		const events = this.timeline.events;
		const i = this.keyIndex(this.appliedKey);
		for (let j = i + 1; j < events.length && j < i + 200; j++) {
			const te = events[j];
			if (te.event.type === "pointer" && !te.event.actor) {
				if (this.scheduledPointer && this.scheduledPointer.seq === te.seq && this.scheduledPointer.idx === te.idx) return;
				this.scheduledPointer = {
					seq: te.seq,
					idx: te.idx
				};
				this.cursor.moveTo(te.event.x, te.event.y, this.currentAbsMs, te.t);
				return;
			}
			if (te.t - this.currentAbsMs > 2e3) return;
		}
	}
	updateGap() {
		const gap = this.timeline.gapAt(this.currentAbsMs) ?? null;
		if (gap !== this.currentGap) {
			this.currentGap = gap;
			this.gapOverlay.style.display = gap ? "flex" : "none";
			const text = this.gapOverlay.firstElementChild;
			if (gap && text) text.textContent = `Not recorded (${(gap.end - gap.start) / 1e3 | 0}s): ${describeGap(gap.reason)}`;
			this.emit("gap", { gap });
		}
	}
	setViewportSize(w, h) {
		if (!w || !h) return;
		this.viewport = {
			w,
			h
		};
		this.stage.style.width = `${w}px`;
		this.stage.style.height = `${h}px`;
		this.mirror.setViewport(w, h);
		this.fit();
	}
	fit() {
		if (!this.opts.fit) {
			this.wrapper.style.transform = "";
			return;
		}
		const cw = this.container.clientWidth || this.viewport.w;
		const ch = this.container.clientHeight || this.viewport.h;
		let scale = Math.min(cw / this.viewport.w, ch / this.viewport.h);
		if (!this.opts.allowUpscale) scale = Math.min(1, scale);
		if (!Number.isFinite(scale) || scale <= 0) scale = 1;
		const x = Math.max(0, (cw - this.viewport.w * scale) / 2);
		const y = Math.max(0, (ch - this.viewport.h * scale) / 2);
		this.wrapper.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
	}
	destroy() {
		this.destroyed = true;
		this.cancelTick();
		this.resizeObs?.disconnect();
		this.canvasTracks.reset();
		this.mirror.destroy();
		this.cursor.destroy();
		this.highlight.destroy();
		this.wrapper.remove();
		this.urlBar.remove();
		this.gapOverlay.remove();
		this.listeners.clear();
	}
};
function nowMs() {
	return typeof performance !== "undefined" ? performance.now() : Date.now();
}
function describeGap(reason) {
	switch (reason) {
		case "memory": return "device memory pressure";
		case "offline": return "device was offline";
		case "quota": return "local storage quota reached";
		case "paused": return "recording paused";
		case "hidden": return "tab was in the background";
		default: return reason;
	}
}
function formatTime(ms) {
	const total = Math.max(0, Math.floor(ms / 1e3));
	const h = Math.floor(total / 3600);
	const m = Math.floor(total % 3600 / 60);
	const s = total % 60;
	const mm = h ? String(m).padStart(2, "0") : String(m);
	return `${h ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}
//#endregion
export { formatRange as A, WebCodecsFrameDecoder as C, DEFAULT_HALF_MS as D, defaultImageDecoder as E, KEY_STEP_MS as O, VideoTrack as S, MARK as T, rewriteCssUrls as _, COMMON_STYLE_KEYS as a, CanvasTracks as b, attributesOf as c, cssEscape as d, describeElement as f, Mirror as g, xpathFor as h, Timeline as i, CursorOverlay as j, SelectionModel as k, boxModelOf as l, selectorFor as m, describeGap as n, accessibleName as o, isHashedToken as p, formatTime as r, ancestorsOf as s, Player as t, computedStylesOf as u, rewriteSrcset as v, CommandReplayer as w, reasonLabel as x, HighlightOverlay as y };

//# sourceMappingURL=player-BVT01yhQ.js.map